import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Mail, ArrowUpRight, Sparkles, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { createContact } from "../lib/api";

const PHONE = "551-689-7544";
const PHONE_HREF = "tel:+15516897544";
const EMAIL = "blinkshotsusa@gmail.com";
const EMAIL_HREF = "mailto:blinkshotsusa@gmail.com";

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  inquiry_type: "general",
  service: "",
  budget: "",
  location: "",
  social_link: "",
  message: "",
  website: "", // honeypot
};

export const Contact = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.4], [10, 0]);

  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // If user lands on #contact via "Contact Us" button on Custom Shoot card, switch the form mode
  useEffect(() => {
    const checkHash = () => {
      if (window.location.hash === "#contact-custom") {
        setField("inquiry_type", "custom-shoot");
        const el = document.getElementById("contact");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    checkHash();
    window.addEventListener("hashchange", checkHash);
    return () => window.removeEventListener("hashchange", checkHash);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and your message.");
      return;
    }
    try {
      setLoading(true);
      await createContact(form);
      setShowSuccess(true);
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="relative py-28 md:py-36 perspective-scene"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#100202,transparent_60%)] pointer-events-none" />
      <div
        aria-hidden
        className="absolute -top-20 -left-40 h-[420px] w-[420px] rounded-full blur-[130px] opacity-22 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute -bottom-20 -right-40 h-[460px] w-[460px] rounded-full blur-[130px] opacity-22 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[420px] w-[720px] rounded-full blur-[140px] opacity-18 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #7a0000, transparent 70%)" }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      <div className="neon-streak top-[5%] left-[-10%] right-[-10%] opacity-40" />

      <div className="relative mx-auto max-w-6xl px-5">
        <motion.div
          style={{ rotateX }}
          className="text-center mb-12 md:mb-16 max-w-3xl mx-auto"
        >
          <p className="mono-eyebrow mb-4" data-testid="contact-eyebrow">
            Contact
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.02] tracking-tight">
            Let's Create Something{" "}
            <span className="text-[#E10600]">Cinematic</span>
          </h2>
          <p className="mt-5 text-neutral-400 font-body text-base md:text-lg">
            Need a fully customized shoot or special production request? Reach out directly and
            we'll build a personalized package for you.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Contact info column */}
          <div className="lg:col-span-5 flex flex-col gap-4">
            <ContactInfoCard
              icon={Phone}
              label="Call Now"
              value={PHONE}
              href={PHONE_HREF}
              cta="Call Now"
              testid="contact-phone"
              ctaTestId="contact-call-now-button"
              note="Available weekdays · Quick replies on weekends"
              delay={0}
            />
            <ContactInfoCard
              icon={Mail}
              label="Email Us"
              value={EMAIL}
              href={EMAIL_HREF}
              cta="Email Us"
              testid="contact-email"
              ctaTestId="contact-email-us-button"
              note="We reply within a few hours on weekdays"
              delay={0.1}
            />

            <div className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono text-[#E10600]">
              <Sparkles className="h-3 w-3" />
              Custom production · Tailored quote
            </div>

            <p className="mt-2 mono-eyebrow text-neutral-500">
              BlinkShots · NYC · 1st US-based content creation brand
            </p>
          </div>

          {/* Form column */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40, rotateX: 10 }}
            whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 relative rounded-3xl glass p-6 md:p-9"
            data-testid="contact-form"
          >
            <div className="absolute -top-20 -right-20 h-44 w-44 rounded-full bg-[#E10600]/15 blur-3xl pointer-events-none" />

            {!showSuccess ? (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <h3 className="font-display text-2xl md:text-3xl text-white">
                    Send us a message
                  </h3>
                  <InquiryTypeToggle
                    value={form.inquiry_type}
                    onChange={(v) => setField("inquiry_type", v)}
                  />
                </div>

                {/* honeypot — hidden field bots tend to fill */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hidden"
                />

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 relative">
                  <Field
                    label="Full name"
                    value={form.name}
                    onChange={(v) => setField("name", v)}
                    name="name"
                    required
                  />
                  <Field
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    name="email"
                    required
                  />
                  <Field
                    label="Phone"
                    value={form.phone}
                    onChange={(v) => setField("phone", v)}
                    name="phone"
                  />
                  <Field
                    label="Instagram / Social link"
                    value={form.social_link}
                    onChange={(v) => setField("social_link", v)}
                    name="social_link"
                    placeholder="@blinkshotsusa or full URL"
                  />
                  <SelectField
                    label="Service interested in"
                    options={[
                      { v: "", label: "Select…" },
                      { v: "1 Hour Shoot", label: "1 Hour Shoot — $200" },
                      { v: "1–3 Hour Shoot", label: "1–3 Hour Shoot — $350" },
                      { v: "Half Day Shoot", label: "Half Day Shoot — $700" },
                      { v: "Custom Shoot", label: "Custom Shoot" },
                      { v: "Not sure yet", label: "Not sure yet" },
                    ]}
                    value={form.service}
                    onChange={(v) => setField("service", v)}
                    name="service"
                  />
                  <Field
                    label="Budget"
                    value={form.budget}
                    onChange={(v) => setField("budget", v)}
                    name="budget"
                    placeholder="$ approx."
                  />
                  <div className="md:col-span-2">
                    <Field
                      label="Shoot location"
                      value={form.location}
                      onChange={(v) => setField("location", v)}
                      name="location"
                      placeholder="City, venue, on-site, etc."
                    />
                  </div>
                  <div className="md:col-span-2">
                    <TextArea
                      label={
                        form.inquiry_type === "custom-shoot"
                          ? "Tell us about your custom shoot"
                          : "Message"
                      }
                      value={form.message}
                      onChange={(v) => setField("message", v)}
                      name="message"
                      rows={4}
                      required
                    />
                  </div>
                </div>

                <div className="relative mt-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-neutral-500 font-mono max-w-md">
                    Your submission is delivered straight to the BlinkShots team inbox.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="btn-pricing inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium text-white disabled:opacity-70"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    <span>{loading ? "Sending…" : "Send Message"}</span>
                  </button>
                </div>
              </>
            ) : (
              <SuccessPanel onReset={() => setShowSuccess(false)} />
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const ContactInfoCard = ({ icon: Icon, label, value, href, cta, testid, ctaTestId, note, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-15%" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className="group relative overflow-hidden rounded-3xl glass p-6 md:p-7"
    data-testid={testid}
  >
    <div
      aria-hidden
      className="absolute -top-24 -right-20 h-40 w-40 rounded-full bg-[#E10600]/18 blur-3xl pointer-events-none transition-opacity duration-500 opacity-60 group-hover:opacity-90"
    />
    <div className="relative flex items-start gap-4">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#E10600] to-[#7a0000] border border-white/10 shadow-[0_8px_24px_-6px_rgba(225,6,0,0.5)] transition-transform duration-500 group-hover:scale-105 flex-shrink-0"
      >
        <Icon className="h-5 w-5 text-white" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="mono-eyebrow">{label}</p>
        <a
          href={href}
          className="block mt-1 font-display text-xl md:text-2xl text-white hover:text-[#E10600] transition-colors duration-300 break-all"
        >
          {value}
        </a>
        <p className="mt-1 text-xs text-neutral-500 font-mono">{note}</p>
      </div>
      <a
        href={href}
        data-testid={ctaTestId}
        className="btn-pricing inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-xs font-medium text-white no-underline flex-shrink-0"
      >
        <span>{cta}</span>
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    </div>
  </motion.div>
);

const InquiryTypeToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-full border border-white/10 bg-black/30 p-1 text-xs">
    {[
      { v: "general", l: "General" },
      { v: "custom-shoot", l: "Custom Shoot" },
      { v: "booking", l: "Booking" },
    ].map((o) => {
      const active = value === o.v;
      return (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          data-testid={`contact-inquiry-${o.v}`}
          className={`px-3 py-1.5 rounded-full transition-all duration-300 ${
            active
              ? "bg-[#E10600] text-white shadow-[0_4px_14px_rgba(225,6,0,0.45)]"
              : "text-neutral-300 hover:text-white"
          }`}
        >
          {o.l}
        </button>
      );
    })}
  </div>
);

const SuccessPanel = ({ onReset }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="relative text-center py-10"
    data-testid="contact-success-panel"
  >
    <div className="mx-auto h-16 w-16 rounded-full bg-[#E10600]/15 border border-[#E10600]/40 grid place-items-center">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-8 w-8 text-[#E10600]">
        <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <h4 className="mt-5 font-display text-2xl md:text-3xl text-white">Successfully Sent.</h4>
    <p className="mt-2 text-neutral-400 font-body max-w-md mx-auto">
      The BlinkShots team will contact you shortly.
    </p>
    <button
      onClick={onReset}
      className="mt-6 inline-flex items-center gap-2 rounded-full glass px-5 py-2.5 text-sm text-white hover:bg-white/[0.06]"
      data-testid="contact-success-reset"
    >
      Send another message
    </button>
  </motion.div>
);

const Field = ({ label, value, onChange, name, type = "text", required, placeholder }) => (
  <label className="flex flex-col gap-1.5">
    <span className="mono-eyebrow text-neutral-500">
      {label} {required && <span className="text-[#E10600]">*</span>}
    </span>
    <input
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`contact-input-${name}`}
      className="bg-transparent border-b border-white/10 py-2 text-white placeholder:text-neutral-600 focus:border-[#E10600] focus:outline-none transition-colors duration-300 font-body"
    />
  </label>
);

const SelectField = ({ label, options, value, onChange, name }) => (
  <label className="flex flex-col gap-1.5">
    <span className="mono-eyebrow text-neutral-500">{label}</span>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`contact-select-${name}`}
        className="w-full appearance-none bg-transparent border-b border-white/10 py-2 pr-8 text-white focus:border-[#E10600] focus:outline-none font-body cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v} className="bg-[#0a0a0a] text-white">
            {o.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500">▾</span>
    </div>
  </label>
);

const TextArea = ({ label, value, onChange, name, rows = 3, required }) => (
  <label className="flex flex-col gap-1.5">
    <span className="mono-eyebrow text-neutral-500">
      {label} {required && <span className="text-[#E10600]">*</span>}
    </span>
    <textarea
      rows={rows}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`contact-textarea-${name}`}
      className="bg-transparent border-b border-white/10 py-2 text-white placeholder:text-neutral-600 focus:border-[#E10600] focus:outline-none transition-colors duration-300 font-body resize-none"
      placeholder="Tell us about your shoot, brand, vision…"
    />
  </label>
);

export default Contact;
